import {
    useEffect,
    useMemo,
    useRef,
    type FC
} from "react";
import type {
    ScrollView
} from "react-native";
import type IMessageScrollerProviderProps from "./type";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import type {
    MessageScrollerEngineConfig,
    MessageScrollerContextType
} from "../../type";
import {
    createMessageScrollerEngine
} from "../../engine";
import {
    createMessageScrollerStore
} from "../../store";
import {
    MessageScrollerContext
} from "../../context";

const MessageScrollerProvider: FC<IMessageScrollerProviderProps> = ({
    defaultScrollPosition = "last-anchor",
    isPreserveScrollOnPrepend = true,
    hasOlderMessages = true,
    scrollPreviousItemPeek,
    stackBehaviour = "top",
    isAutoScroll = true,
    isLoading = false,
    customTheme,
    onLoadOlder,
    children
}) => {
    const {
        durations,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduced = useReducedMotion();

    const viewportRef = useRef<ScrollView | null>(null);

    const store = useMemo(createMessageScrollerStore, []);

    const config: MessageScrollerEngineConfig = {
        peek: scrollPreviousItemPeek ?? spaces.spacingXxl,
        isPreserveScrollOnPrepend,
        padding: spaces.spacingLg,
        defaultScrollPosition,
        hasOlderMessages,
        stackBehaviour,
        isAutoScroll,
        onLoadOlder,
        isReduced,
        durations
    };

    const configRef = useRef(config);

    configRef.current = config;

    const result = useMemo(() => createMessageScrollerEngine({
        getConfig: () => configRef.current,
        viewportRef,
        store
    }), [
        store
    ]);

    useEffect(() => {
        result.revive();

        return () => {
            result.destroy();
        };
    }, [
        result
    ]);

    const context = useMemo<MessageScrollerContextType>(() => ({
        commands: result.commands,
        engine: result.engine,
        hasOlderMessages,
        stackBehaviour,
        isLoading,
        store
    }), [
        stackBehaviour,
        hasOlderMessages,
        isLoading,
        result,
        store
    ]);

    return <MessageScrollerContext.Provider
        value={context}
    >
        {children}
    </MessageScrollerContext.Provider>;
};
export default MessageScrollerProvider;
